/**
 * Session state for the front end. The API owns the truth — this hook just asks /api/v1/me and
 * exposes the answer. Tokens never exist in the browser; the session is an HttpOnly cookie.
 */
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

export type Me = {
  id: string;
  email: string;
  display_name: string;
  role: "admin" | "member";
};

export type AuthState =
  | { status: "loading" }
  | { status: "signed_in"; user: Me }
  | { status: "signed_out" }
  | { status: "unreachable" };

type AuthContextValue = {
  auth: AuthState;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return value;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });

  const refresh = useCallback(async () => {
    try {
      const response = await fetch("/api/v1/me");
      if (response.ok) {
        setAuth({ status: "signed_in", user: (await response.json()) as Me });
      } else if (response.status === 401) {
        setAuth({ status: "signed_out" });
      } else {
        setAuth({ status: "unreachable" });
      }
    } catch {
      setAuth({ status: "unreachable" });
    }
  }, []);

  const signOut = useCallback(async () => {
    await fetch("/auth/logout", { method: "POST" });
    setAuth({ status: "signed_out" });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return <AuthContext.Provider value={{ auth, refresh, signOut }}>{children}</AuthContext.Provider>;
}
